import { useCallback, useMemo } from 'react';
import { useSoftphoneCallState } from '..';
import { EstablishedCall, isEstablishedCall } from '../../../types';
import {
  getMergedStream,
  getSessionReceiversMediaStream,
  toggleReceiverTracks,
  toggleSenderTracks,
} from '../../../utils/stream-utils';
import { useSoftphoneAudio } from '../../softphone-audio-provider';
import { useSoftphoneClient } from '../../softphone-client-provider';
import { useSoftphoneEventsEmitter } from '../../softphone-events-provider';
import { useSoftphoneMediaDevices } from '../../softphone-media-devices-provider';
import { useReInvite } from './use-reinvite';

/**
 *
 * Updates the session description handler to hold or unhold the call, and then sends a re-invite
 */
export const useToggleHold = () => {
  const { reInviteCall } = useReInvite();
  const updateCall = useSoftphoneCallState((ctx) => ctx.updateCall);
  const establishedCalls = useSoftphoneCallState((ctx) => ctx.establishedCalls);
  const isMuted = useSoftphoneCallState((ctx) => ctx.isMuted);
  const stream = useSoftphoneAudio((ctx) => ctx.stream);
  const { emit } = useSoftphoneEventsEmitter();
  const mergedCallGroup = useSoftphoneCallState((ctx) => ctx.mergedCallGroup);
  const { reInviteCalls } = useReInvite();
  const setPrimaryCall = useSoftphoneCallState((ctx) => ctx.setPrimaryCall);
  const setMergedCallIds = useSoftphoneCallState((ctx) => ctx.setMergedCallIds);
  const client = useSoftphoneClient((ctx) => ctx.client);

  const currInputSource = useSoftphoneMediaDevices((ctx) => ctx?.currInputSource?.deviceId);

  const toggleHold = useCallback(
    (call: EstablishedCall, hold: boolean) => {
      const session = call.session;
      session.sessionDescriptionHandlerOptionsReInvite = {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore: this is valid, and necessary for the re-invite
        hold,
      };

      return reInviteCall(call).then((_res) => {
        toggleReceiverTracks(call.session, !hold);
        toggleSenderTracks(call.session, !hold && !isMuted);
        if (!hold) {
          const mediaStream = getSessionReceiversMediaStream(session);
          stream('remote', mediaStream);
          emit('active-call.unhold', call);
        } else {
          emit('active-call.hold', call);
        }
        return updateCall(call, { onHoldSince: hold ? call.onHoldSince ?? new Date() : undefined });
      });
    },
    [emit, isMuted, reInviteCall, stream, updateCall]
  );

  const toggleHoldAll = useCallback(
    async (hold: boolean) => {
      const res: EstablishedCall[] = [];
      for (const call of establishedCalls) {
        res.push(await toggleHold(call, hold));
      }
      return res;
    },
    [toggleHold, establishedCalls]
  );

  const toggleHoldMergedCalls = useCallback(
    async (hold: boolean) => {
      if (!mergedCallGroup?.length) {
        return;
      }

      const calls = mergedCallGroup.filter(isEstablishedCall).map((call) => {
        call.session.sessionDescriptionHandlerOptionsReInvite = {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore: this is valid, and necessary for the re-invite
          hold,
        };
        return call;
      });

      await reInviteCalls(calls).then(async () => {
        for (const call of calls) {
          updateCall(call, { onHoldSince: hold ? call.onHoldSince ?? new Date() : undefined });
          if (!hold) {
            emit('active-call.unhold', call);
          } else {
            emit('active-call.hold', call);
          }
        }

        if (!hold && client) {
          const sessions = calls.map((call) => call.session);

          /**
           * Replace local audio track for every session in the merged group
           */

          // Acquire local audio track once
          const localStream = await navigator.mediaDevices.getUserMedia({
            audio: currInputSource ? { deviceId: currInputSource } : true,
            video: false,
          });
          const localAudioTrack = localStream.getAudioTracks()[0];

          // Attach local audio track to every session in the merged group
          for (const session of sessions) {
            // @ts-expect-error - there should be a `peerConnection`, it's just not exposed in types. As a precaution, we're checking for it on the next line.
            const peerConnection = session.sessionDescriptionHandler?.peerConnection as RTCPeerConnection;
            if (!peerConnection) continue;
            const sender = peerConnection.getSenders().find((s) => s.track && s.track.kind === 'audio');
            if (sender) {
              await sender.replaceTrack(localAudioTrack);
            } else {
              peerConnection.addTrack(localAudioTrack);
            }

            /**
             * After replacing the audio track in the peer connection, we also need to update the one saved in the sipjs client.
             * There's no utility to replace tracks in the sipjs client, so we have to remove the old track and add the new one.
             */
            const oldTrack = client.getLocalMediaStream(session)?.getAudioTracks()[0];
            if (oldTrack) {
              client.getLocalMediaStream(session)?.removeTrack(oldTrack);
            }
            client.getLocalMediaStream(session)?.addTrack(localAudioTrack);

            toggleSenderTracks(session, !isMuted);
          }
          const mediaStream = getMergedStream(sessions);
          stream('remote', mediaStream);
          setMergedCallIds(calls.map((call: any) => call.id));
        }
      });
    },
    [mergedCallGroup, reInviteCalls, client, updateCall, emit, currInputSource, stream, setMergedCallIds, isMuted]
  );

  const unHoldAndSetAsPrimary = useCallback(
    (call: EstablishedCall) => {
      toggleHoldAll(true).then(() => {
        toggleHold(call, false);
        setPrimaryCall(call.id);
      });
    },
    [toggleHold, toggleHoldAll]
  );

  return useMemo(
    () => ({ toggleHold, toggleHoldAll, toggleHoldMergedCalls, unHoldAndSetAsPrimary }),
    [toggleHold, toggleHoldAll, toggleHoldMergedCalls, unHoldAndSetAsPrimary]
  );
};
